import React from 'react';
import { Input, Modal, Typography } from 'antd';

const { Paragraph, Text } = Typography;

/**
 * WP-08 — client half of the odometer confirmation contract.
 *
 * The API answers 409 with `code: 'MILEAGE_DECREASE_REQUIRES_REASON'` when a
 * reading is lower than the last recorded one. That is not a rejection: odometer
 * rollback and instrument-cluster replacement are real, so the save is allowed
 * to proceed once the user says why. These helpers turn that 409 into a prompt
 * and the answer into the fields the retry needs.
 *
 * Branching is on `code`, never on the message text, so wording can change
 * without breaking the flow.
 */

export const MILEAGE_DECREASE_CODE = 'MILEAGE_DECREASE_REQUIRES_REASON';

export interface MileageDecreaseConflict {
  message: string;
  previousMileage: number | null;
  previousRecordedAt: string | null;
}

/** Fields sent on the retry to confirm a backwards reading. */
export interface MileageConfirmation {
  mileage_reason: string;
  allow_mileage_decrease: true;
}

function responseBody(error: unknown): any {
  return (error as any)?.response?.data ?? null;
}

/**
 * Returns the conflict details when `error` is a mileage-decrease 409, else null.
 */
export function parseMileageDecreaseConflict(
  error: unknown
): MileageDecreaseConflict | null {
  const body = responseBody(error);
  if (!body || body.code !== MILEAGE_DECREASE_CODE) return null;

  return {
    message: body.error ?? 'This reading is lower than the last one recorded.',
    previousMileage: body.previous_reading?.mileage ?? null,
    previousRecordedAt: body.previous_reading?.recorded_at ?? null,
  };
}

/**
 * A server-supplied mileage error message, for the codes that are NOT a
 * confirmable conflict (negative, absurd, non-numeric). Returns null when the
 * error is not a mileage error, so callers keep their own fallback wording.
 */
export function getMileageErrorMessage(error: unknown): string | null {
  const body = responseBody(error);
  if (!body || typeof body.code !== 'string') return null;
  if (!body.code.startsWith('MILEAGE_')) return null;
  return body.error ?? null;
}

/**
 * Ask the user to confirm a backwards reading.
 *
 * Resolves with the confirmation fields, or null if they cancel. A reason is
 * required to proceed — "confirmed but unexplained" is the silent acceptance the
 * acceptance criteria rule out, and the reason is what makes the log row
 * readable a year later.
 */
export function confirmMileageDecrease(
  conflict: MileageDecreaseConflict
): Promise<MileageConfirmation | null> {
  return new Promise((resolve) => {
    let reason = '';

    const modal = Modal.confirm({
      title: 'Confirm lower mileage',
      width: 460,
      okText: 'Record it',
      cancelText: 'Go back',
      // Cannot save without a reason; enabled by the onChange below.
      okButtonProps: { disabled: true },
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>{conflict.message}</Paragraph>
          {conflict.previousMileage != null && (
            <Paragraph style={{ marginBottom: 12 }}>
              <Text type="secondary">
                Last recorded: {conflict.previousMileage.toLocaleString()} mi
                {conflict.previousRecordedAt ? ` on ${conflict.previousRecordedAt}` : ''}
              </Text>
            </Paragraph>
          )}
          <Input.TextArea
            rows={2}
            maxLength={500}
            placeholder="Why is it lower? e.g. instrument cluster replaced, odometer rolled over"
            onChange={(e) => {
              reason = e.target.value;
              modal.update({ okButtonProps: { disabled: reason.trim() === '' } });
            }}
          />
        </div>
      ),
      onOk: () => resolve({ mileage_reason: reason.trim(), allow_mileage_decrease: true }),
      onCancel: () => resolve(null),
    });
  });
}
